/**
 * Status state machines — single source of truth for every entity lifecycle
 * (docs/01 §4, docs/02). Ported/adapted from the old app's `status.js`.
 *
 * The API's only legal way to change a status is via `assertTransition`.
 * SQL CHECK constraints mirror the vocabularies for DB-level integrity.
 *
 * Vocabulary follows the NEW data model (docs/02 §4): the application
 * collection state is `PendingFundVerification` (old app called it
 * PendingFundVerification).
 */
export const STATUS_MACHINES = {
    application: {
        Draft: { label: 'Draft', next: ['PendingApproval', 'PendingFundVerification', 'Cancelled'] },
        PendingApproval: {
            label: 'Pending Approval',
            // Approving the investment IS the go-live: money is verified and the NCD
            // becomes Active in one step (owner spec 2026-07-19). The older
            // PendingFundVerification/PendingActivation legs remain for in-flight and
            // integration data but are no longer part of the staff-enrolment flow.
            next: ['Active', 'PendingFundVerification', 'PendingEsign', 'PendingActivation', 'PendingAllotment', 'Cancelled', 'Rejected'],
        },
        PendingFundVerification: {
            label: 'Pending Fund Verification',
            next: ['PendingEsign', 'PendingActivation', 'PendingAllotment', 'Cancelled', 'Rejected'],
        },
        PendingEsign: { label: 'Awaiting eSign', next: ['PendingActivation', 'PendingAllotment', 'Cancelled'] },
        // Funded, awaiting the maker-checker activation approval. Activation (not
        // allotment) is what turns money-in-the-account into a live NCD.
        PendingActivation: { label: 'Pending Activation', next: ['Active', 'Cancelled', 'Rejected'] },
        PendingAllotment: { label: 'Pending Allotment', next: ['Active', 'Cancelled'] },
        Active: {
            label: 'Active',
            next: ['Matured', 'Redeemed', 'RolledOver', 'PrematureWithdrawn', 'Transferred'],
        },
        Matured: { label: 'Matured', next: ['Redeemed', 'RolledOver'] },
        Redeemed: { label: 'Redeemed', next: [], terminal: true },
        RolledOver: { label: 'Rolled Over', next: [], terminal: true },
        PrematureWithdrawn: { label: 'Premature Withdrawal', next: [], terminal: true },
        Transferred: { label: 'Transferred', next: [], terminal: true },
        Cancelled: { label: 'Cancelled', next: [], terminal: true },
        Rejected: { label: 'Rejected', next: [], terminal: true },
    },
    application_line: {
        Active: { label: 'Active', next: ['Matured', 'PrematureWithdrawn', 'RolledOver'] },
        Matured: { label: 'Matured', next: [], terminal: true },
        PrematureWithdrawn: { label: 'Premature Withdrawal', next: [], terminal: true },
        RolledOver: { label: 'Rolled Over', next: [], terminal: true },
    },
    disbursement: {
        Scheduled: { label: 'Scheduled', next: ['Paid', 'Failed', 'Skipped'] },
        Paid: { label: 'Paid', next: [], terminal: true },
        Failed: { label: 'Failed', next: ['Scheduled'] },
        Skipped: { label: 'Skipped', next: [], terminal: true },
    },
    payout_batch: {
        Draft: { label: 'Draft', next: ['PendingChecker', 'Cancelled'] },
        PendingChecker: { label: 'Pending Checker', next: ['Approved', 'Failed', 'Cancelled'] },
        Approved: { label: 'Approved', next: ['Downloaded', 'Cancelled'] },
        Downloaded: { label: 'Downloaded', next: ['Reconciled', 'Failed'] },
        Reconciled: { label: 'Reconciled', next: [], terminal: true },
        Failed: { label: 'Failed', next: [], terminal: true },
        Cancelled: { label: 'Cancelled', next: [], terminal: true },
    },
    // Generic maker-checker request. Levels are config-driven (docs/03 §4);
    // this machine only encodes the coarse pending→terminal shape.
    approval_request: {
        Pending: { label: 'Pending', next: ['Approved', 'Rejected'] },
        Approved: { label: 'Approved', next: [], terminal: true },
        Rejected: { label: 'Rejected', next: [], terminal: true },
    },
    series: {
        // A new series waits for a checker before it can take investments (owner
        // 2026-08-19). Its only moves are made BY the approval — approve → Open,
        // reject → Withdrawn — so Masters offers no manual transition out of it and
        // nobody can hand-wave a series into service.
        PendingApproval: { label: 'Awaiting approval', next: [] },
        // No 'Closing' (owner 2026-08-20): it was a staging state between Open and
        // Allotted that nothing depended on, and every extra rung invites the
        // shortcut that skipped NCD 28's allotment date. Withdrawn stays.
        Open: { label: 'Open', next: ['Allotted', 'Withdrawn'] },
        Allotted: { label: 'Allotted', next: ['Open', 'Closed', 'Withdrawn'] },
        Closed: { label: 'Closed', next: ['Open', 'Allotted', 'Withdrawn'] },
        Withdrawn: { label: 'Withdrawn', next: [], terminal: true },
    },
    customer_kyc: {
        Pending: { label: 'Pending', next: ['InProgress', 'Verified', 'Rejected'] },
        InProgress: { label: 'In Progress', next: ['Verified', 'Rejected', 'Expired'] },
        Verified: { label: 'Verified', next: ['Rejected'] },
        Rejected: { label: 'Rejected', next: ['Pending'] },
        Expired: { label: 'Expired', next: ['Pending'] },
    },
    redemption: {
        Requested: { label: 'Requested', next: ['Approved', 'Rejected'] },
        Approved: { label: 'Approved', next: ['Paid'] },
        Paid: { label: 'Paid', next: [], terminal: true },
        Rejected: { label: 'Rejected', next: [], terminal: true },
    },
};
/**
 * Application statuses that count as a live, outstanding NCD on the book — the
 * "Active (net)" figure the old app reports. Byte-compatible with the legacy
 * rule (see integration/shared.ts `customerFacingStatus`): funded money reads
 * as outstanding from the moment it lands, wherever it currently sits in the
 * approval/allotment pipeline, until it exits (Matured/Redeemed/RolledOver/
 * PrematureWithdrawn/Transferred) or is killed (Cancelled/Rejected). `Draft`
 * is pre-funding and excluded — that is the "raised minus cancelled" gap seen
 * on an open series (e.g. NCD 27: raised ₹7.59 Cr vs active-net ₹7.58 Cr).
 *
 * Consequence: money subscribed to a still-Open series (pre-allotment) is part
 * of the outstanding book, so the dashboard/export match the old app rather
 * than showing ₹0 for an open series.
 */
export const OUTSTANDING_APPLICATION_STATUSES = [
    // NB: 'PendingApproval' is intentionally EXCLUDED. That status is a
    // subscription still awaiting maker-checker approval with no money received
    // yet — it is neither funded nor approved, so it must not inflate the
    // outstanding book. The book starts once money is in / the app is approved.
    'PendingFundVerification',
    'PendingEsign',
    'PendingActivation',
    'PendingAllotment',
    'Active',
];
export function canTransition(entity, from, to) {
    const node = STATUS_MACHINES[entity][from];
    return !!node && node.next.includes(to);
}
export function isTerminal(entity, status) {
    const node = STATUS_MACHINES[entity][status];
    return !!node && (node.terminal === true || node.next.length === 0);
}
export function statusLabel(entity, status) {
    const node = STATUS_MACHINES[entity][status];
    return node ? node.label : status;
}
export function validTransitions(entity, from) {
    const node = STATUS_MACHINES[entity][from];
    return node ? [...node.next] : [];
}
//# sourceMappingURL=statusMachine.js.map